import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import "./ChatbotWidget.scss";

const CHATBOT_URL = "https://d1x8svzs28gqii.cloudfront.net";
const CHATBOT_ORIGIN = "https://d1x8svzs28gqii.cloudfront.net";

// Endpoint that returns { token: "<JWT>" } (the one you saw in the Network tab)
const CHATBOT_INIT_URL =
  "https://i8nusvk8lb.execute-api.eu-west-1.amazonaws.com/prod/web/init";
// Check Network tab -> /web/init -> "Request Method" and copy it here
const CHATBOT_INIT_METHOD = "POST";
// localStorage key where the chatbot JWT is stored
const CHATBOT_JWT_KEY = "chatbotjwt";

const HIDDEN_PATHS = ["/login", "/dev/login", "/cLogin", "/register"];

// Turn all the diagnostic logs on/off (set to false in production)
const DEBUG_CHATBOT = true;

// Log tags so every line says which contract it belongs to
const TAG = {
  CFG: "[CONFIG]",
  DOCS: "[DOCS]", //       TL-Omantel-KB Technical Documentation (AUTH_INIT)
  INDEX: "[INDEX.HTML]", // existing index.html host page (get-token / set_token / open)
  CLOSE: "[CLOSE]", //     hide_chatbot / open:false
  MSG: "[CHATBOT→PARENT]",
};

const log = (tag, icon, text, extra) => {
  if (!DEBUG_CHATBOT) return;
  if (extra !== undefined) console.log(`${tag} ${icon} ${text}`, extra);
  else console.log(`${tag} ${icon} ${text}`);
};

const maskToken = (t) =>
  t ? `${String(t).slice(0, 10)}… (len ${String(t).length})` : null;

const getCookie = (name) => {
  const prefix = `${name}=`;
  const match = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(prefix));
  return match ? match.substring(prefix.length) : null;
};

// Read sessionId + seconds left from a JWT (no verification, display only)
const jwtInfo = (token) => {
  try {
    const part = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(atob(part));
    return {
      sessionId: json.sessionId,
      expiresIn: json.exp - Math.floor(Date.now() / 1000),
    };
  } catch (e) {
    return null;
  }
};

// Call the init endpoint, store the returned JWT in localStorage
const fetchChatbotJwt = async () => {
  const options = {
    method: CHATBOT_INIT_METHOD,
    headers: { "Content-Type": "application/json" },
  };
  if (CHATBOT_INIT_METHOD === "POST") options.body = JSON.stringify({});

  const res = await fetch(CHATBOT_INIT_URL, options);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  const data = await res.json();
  if (!data || !data.token) throw new Error("response has no 'token' field");

  localStorage.setItem(CHATBOT_JWT_KEY, data.token);
  log(
    TAG.DOCS,
    "✅",
    `JWT fetched from /web/init and saved in localStorage '${CHATBOT_JWT_KEY}'`,
    jwtInfo(data.token),
  );
  return data.token;
};

// Use the stored JWT while it is still valid, otherwise fetch a new one
// (the JWT only lives ~60 seconds)
const ensureChatbotJwt = async () => {
  const stored = localStorage.getItem(CHATBOT_JWT_KEY);
  const info = stored ? jwtInfo(stored) : null;

  if (stored && info && info.expiresIn > 10) {
    log(
      TAG.DOCS,
      "ℹ️",
      `using stored JWT '${CHATBOT_JWT_KEY}' (expires in ${info.expiresIn}s)`,
    );
    return stored;
  }

  try {
    return await fetchChatbotJwt();
  } catch (e) {
    log(
      TAG.DOCS,
      "❌",
      `could not fetch JWT from /web/init: ${e.message} (CORS, wrong method/body, or our origin is not allowed?)`,
    );
    return stored;
  }
};

// DOCS: session data the parent must send after login.
// Adjust the localStorage keys to what your app really stores.
const getSessionPayload = () => ({
  token: localStorage.getItem(CHATBOT_JWT_KEY), // JWT
  agentId: "69925",
  // agentName: localStorage.getItem("userName") || "",
  agentName: "Hamed Mohammed Ahmed Al Shahri",
  department: localStorage.getItem("department") || "",
  language: localStorage.getItem("language") || "en",
});

const eventName = (data) => {
  if (typeof data === "string") return data;
  if (!data) return "no-data";
  return (
    data.action ||
    data.type ||
    data.event ||
    data.id ||
    Object.keys(data).join(", ")
  );
};

const newStatus = () => ({
  iframeLoaded: false,
  authInitSent: 0,
  tokenFound: null,
  acceptanceHintShown: false,
  openReceived: 0,
  getTokenRequested: 0,
  getTokenReplied: 0,
  getTokenReplyHadToken: null,
  setTokenSent: 0,
  hideChatbotReceived: 0,
  openFalseReceived: 0,
  names: new Set(),
});

const yn = (b) => (b ? "✅ YES" : "❌ NO");

const ChatbotWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const iframeRef = useRef(null);
  const status = useRef(newStatus());

  // ---------- DOCS: parent -> chatbot (AUTH_INIT) ----------
  const sendAuthInit = async (reason) => {
    const s = status.current;
    const win = iframeRef.current?.contentWindow;
    if (!win) {
      log(
        TAG.DOCS,
        "❌",
        "iframe contentWindow not available, AUTH_INIT not sent",
      );
      return;
    }

    // make sure localStorage.chatbotjwt holds a valid (not expired) JWT
    await ensureChatbotJwt();

    const payload = getSessionPayload();
    s.tokenFound = Boolean(payload.token);
    if (!payload.token) {
      log(
        TAG.DOCS,
        "⚠️",
        `no JWT found in localStorage key '${CHATBOT_JWT_KEY}'. AUTH_INIT goes out without a token`,
      );
    }

    win.postMessage({ type: "AUTH_INIT", payload }, CHATBOT_ORIGIN);
    s.authInitSent += 1;
    log(TAG.DOCS, "✅", `AUTH_INIT sent (${reason}) -> ${CHATBOT_ORIGIN}`, {
      ...payload,
      token: maskToken(payload.token),
    });

    if (!s.acceptanceHintShown) {
      s.acceptanceHintShown = true;
      log(
        TAG.DOCS,
        "ℹ️",
        "The parent cannot see whether the chatbot accepted it. In DevTools switch the console context to the chatbot iframe: 'Session initialized for <name>' = accepted, 'Untrusted message origin' = our origin is not on the chatbot's allow-list.",
      );
    }
  };

  // ---------- Report: run window.__chatbotReport() in the console ----------
  const printReport = () => {
    const s = status.current;
    const stored = localStorage.getItem(CHATBOT_JWT_KEY);
    const info = stored ? jwtInfo(stored) : null;
    const rows = [
      {
        Source: "CONFIG",
        Check: "Our origin (must be allow-listed by the chatbot)",
        Result: window.location.origin,
      },
      {
        Source: "DOCS",
        Check: "iframe loaded (onLoad)",
        Result: yn(s.iframeLoaded),
      },
      {
        Source: "DOCS",
        Check: `JWT stored in localStorage '${CHATBOT_JWT_KEY}'`,
        Result: stored
          ? `✅ YES (${info ? `expires in ${info.expiresIn}s` : "not a readable JWT"})`
          : "❌ NO",
      },
      {
        Source: "DOCS",
        Check: "AUTH_INIT sent to exact origin",
        Result: `${yn(s.authInitSent > 0)} (${s.authInitSent}x)`,
      },
      {
        Source: "DOCS",
        Check: "Chatbot accepted AUTH_INIT",
        Result:
          "❓ UNKNOWN: check the iframe console ('Session initialized' / 'Untrusted message origin')",
      },
      {
        Source: "INDEX.HTML",
        Check: "open event {open:true} received",
        Result: `${yn(s.openReceived > 0)} (${s.openReceived}x)`,
      },
      {
        Source: "INDEX.HTML",
        Check: "get-token request received from chatbot",
        Result: `${yn(s.getTokenRequested > 0)} (${s.getTokenRequested}x)`,
      },
      {
        Source: "INDEX.HTML",
        Check: "get-token reply sent",
        Result: `${yn(s.getTokenReplied > 0)}${s.getTokenReplied ? (s.getTokenReplyHadToken ? " (with LtpaToken2)" : " (token was null: no LtpaToken2 cookie)") : ""}`,
      },
      {
        Source: "INDEX.HTML",
        Check: "set_token sent to chatbot",
        Result: `${yn(s.setTokenSent > 0)} (${s.setTokenSent}x)`,
      },
      {
        Source: "CLOSE",
        Check: "hide_chatbot received (index.html close event)",
        Result: `${yn(s.hideChatbotReceived > 0)} (${s.hideChatbotReceived}x)`,
      },
      {
        Source: "CLOSE",
        Check: "{open:false} received",
        Result: `${yn(s.openFalseReceived > 0)} (${s.openFalseReceived}x)`,
      },
      {
        Source: "ALL",
        Check: "Message names seen from the chatbot",
        Result: [...s.names].join(" | ") || "none",
      },
    ];
    console.table(rows);
  };

  useEffect(() => {
    // ---- config sanity check ----
    if (!CHATBOT_URL || !CHATBOT_ORIGIN) {
      log(TAG.CFG, "❌", "CHATBOT_URL or CHATBOT_ORIGIN is empty");
    } else {
      try {
        const urlOrigin = new URL(CHATBOT_URL).origin;
        if (urlOrigin !== CHATBOT_ORIGIN) {
          log(
            TAG.CFG,
            "❌",
            `CHATBOT_ORIGIN (${CHATBOT_ORIGIN}) does not match the origin of CHATBOT_URL (${urlOrigin}). Messages will be dropped`,
          );
        } else {
          log(TAG.CFG, "✅", `chatbot origin OK: ${CHATBOT_ORIGIN}`);
        }
      } catch (e) {
        log(TAG.CFG, "❌", "CHATBOT_URL is not a valid URL");
      }
    }
    log(
      TAG.CFG,
      "ℹ️",
      `our origin: ${window.location.origin} (the chatbot must allow-list this)`,
    );

    // ---- incoming messages: chatbot -> parent ----
    const handleMessage = (event) => {
      const data = event.data;
      const s = status.current;

      // Ignore browser-extension noise
      if (
        data &&
        typeof data === "object" &&
        String(data.namespace || "").startsWith("__crx")
      ) {
        return;
      }

      const name = eventName(data);

      if (event.origin !== CHATBOT_ORIGIN) {
        log(
          TAG.CFG,
          "ℹ️",
          `ignored message "${name}" from other origin ${event.origin}`,
        );
        return;
      }

      s.names.add(name);
      log(TAG.MSG, "ℹ️", `received "${name}"`, data);

      // INDEX.HTML: chatbot asks the host for the token
      if (data?.id === "iframe-request" && data?.message === "get-token") {
        s.getTokenRequested += 1;
        const token = getCookie("LtpaToken2");
        s.getTokenReplyHadToken = Boolean(token);
        if (event.source) {
          event.source.postMessage({ token, open: "true" }, CHATBOT_ORIGIN);
        }
        s.getTokenReplied += 1;
        log(
          TAG.INDEX,
          token ? "✅" : "⚠️",
          token
            ? "get-token request received -> replied with LtpaToken2"
            : "get-token request received -> replied with token=null (no LtpaToken2 cookie)",
        );
        return;
      }

      // INDEX.HTML: {open:"true", token} -> store cookie + send set_token
      if (data?.open === "true" && data?.token) {
        document.cookie = `LtpaToken2=${data.token}; path=/`;
        const frameWin = iframeRef.current && iframeRef.current.contentWindow;
        if (frameWin) {
          frameWin.postMessage(
            { type: "set_token", token: data.token },
            CHATBOT_ORIGIN,
          );
        }
        s.setTokenSent += 1;
        log(
          TAG.INDEX,
          "✅",
          "open:'true' with token -> cookie stored + set_token sent to chatbot",
        );
      }

      // CLOSE: index.html close event
      if (data?.action === "hide_chatbot") {
        s.hideChatbotReceived += 1;
        log(TAG.CLOSE, "✅", "hide_chatbot received -> closing");
        setIsOpen(false);
        printReport();
        return;
      }

      // CLOSE: {open:false}
      if (data?.open === false) {
        s.openFalseReceived += 1;
        log(TAG.CLOSE, "✅", "open:false received -> closing");
        setIsOpen(false);
        printReport();
        return;
      }

      // OPEN
      if (data?.open === true || data?.open === "true") {
        s.openReceived += 1;
        log(
          TAG.INDEX,
          "✅",
          `open event received (open:${JSON.stringify(data.open)}) -> opening`,
        );
        if (data.open === true && !data.token) {
          log(
            TAG.INDEX,
            "ℹ️",
            "index.html would call requestToken() here; the chatbot should then send a get-token request",
          );
        }
        setIsOpen(true);
        sendAuthInit("chatbot opened (retry)");
        return;
      }

      log(
        TAG.MSG,
        "⚠️",
        `unknown message "${name}" (not open / close / get-token / AUTH)`,
      );
    };

    window.addEventListener("message", handleMessage);
    window.__chatbotReport = printReport;
    log(
      TAG.CFG,
      "ℹ️",
      "Click open and close in the chatbot, then run  __chatbotReport()  in the console for the summary table",
    );

    return () => {
      window.removeEventListener("message", handleMessage);
      delete window.__chatbotReport;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isHidden = HIDDEN_PATHS.some((path) =>
    location.pathname.startsWith(`${process.env.PUBLIC_URL}${path}`),
  );

  if (isHidden) {
    return null;
  }

  const iframeClass = isOpen
    ? "chatbot-widget-frame chatbot-widget-frame--open"
    : "chatbot-widget-frame chatbot-widget-frame--closed";

  const handleLoad = () => {
    status.current.iframeLoaded = true;
    log(TAG.DOCS, "✅", "iframe loaded");
    sendAuthInit("iframe onLoad"); // DOCS Step 2
  };

  return (
    <>
      <iframe
        ref={iframeRef}
        onLoad={handleLoad}
        title="KB Chatbot"
        src={CHATBOT_URL}
        className={iframeClass}
        allow="clipboard-write"
      />

      {/* {isOpen && (
        <button
          type="button"
          className="chatbot-widget-close-btn"
          aria-label="Close chatbot"
          onClick={() => setIsOpen(false)}
        >
          ×
        </button>
      )} */}
    </>
  );
};

export default ChatbotWidget;
